import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import CoverLayout from "components/CoverLayout";
import bgImage from "assets/images/bg-sign-in.png";
import { LanguageContext } from "context";

import { useSessionContext } from "context/SessionContext";
import { supabase } from "lib/supabaseClient";

import { mailLogin } from "@shared/api/auth";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
// ------------------------------------------------

function SignIn(): JSX.Element {
  const { session } = useSessionContext();

  const [rememberMe, setRememberMe] = useState<boolean>(true);
  const [email, setEmail] = useState<string>(" ");
  const [password, setPassword] = useState<string>("");
  
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertColor, setAlertColor] = useState<"info" | "error" | "success">("info");
  const [showAlert, setShowAlert] = useState(false);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);

  const navigate = useNavigate();

  // LanguageContext から t関数と、言語状態・変更関数を取得
  const { t, language, setLanguage } = useContext(LanguageContext) as any;

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault(); // フォーカスが外れるのを防ぐ
  };

  const { refresh } = useSessionContext();

  function getCookie(name: string): string | null {
    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((c) => c.startsWith(`${name}=`));
    return cookie ? decodeURIComponent(cookie.split("=")[1]) : null;
  }

  useEffect(() => {
    const remembered = getCookie("rememberMe") === "true";
    const savedEmail = getCookie("email");

    if (remembered && savedEmail) {
      setRememberMe(true);
      setEmail(savedEmail);
    } else {
      setRememberMe(false);
      setEmail("");
    }
  }, []);

  const handleForgotPasswordClick = () => {
    if (!email.trim()) {
      setAlertColor("error");
      setAlertMessage(t("cover-sign-in.alert_enter_email_for_reset") || "パスワードを再設定するEメールアドレスを入力してください。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      return;
    }
    // エラーがなければカスタムダイアログを開く
    setResetDialogOpen(true);
  };

  // 確認メール再送信クリックハンドラー
  const handleResendEmailClick = () => {
    if (!email.trim()) {
      setAlertColor("error");
      setAlertMessage(t("cover-sign-in.alert_enter_email_for_resend") || "確認メールを再送信するEメールアドレスを入力してください。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      return;
    }
    setResendDialogOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    // まずダイアログを閉じる
    setResetDialogOpen(false);
    
    try {
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), platform: "web", origin: window.location.origin }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || t("cover-sign-in.alert_reset_fail") || "送信失敗");
      }

      setAlertColor("success");
      setAlertMessage(t("cover-sign-in.alert_send_done_msg") || "登録されたメールアドレスに再設定用リンクを送信しました。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);

    } catch (err: any) {
      console.error(`reset password err:`, err);
      setAlertColor("error");
      setAlertMessage(err.message || t("cover-sign-in.alert_reset_fail"));
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 確認メール再送信 実行関数
  const handleConfirmResendEmail = async () => {
    setResendDialogOpen(false);
    
    try {
      setIsSubmitting(true);

      // Supabaseの確認メール再送用APIを実行
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email.trim(),
        options: {
          // リダイレクト先として現在のoriginを設定
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        throw error;
      }

      setAlertColor("success");
      setAlertMessage(t("cover-sign-in.alert_resend_done_msg") || "確認メールを再送信しました。メールボックスをご確認ください。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);

    } catch (err: any) {
      console.error(`resend confirmation err:`, err);
      setAlertColor("error");
      setAlertMessage(err.message || t("cover-sign-in.alert_resend_fail") || "送信失敗");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogin = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);

      const hasEmail = email.trim() !== "";
      const hasPassword = password.trim() !== "";

      setEmailError(!hasEmail);
      setPasswordError(!hasPassword);

      if (!hasEmail || !hasPassword) {
        setAlertColor("error");
        setAlertMessage(t("cover-sign-in.alert_input_req") || "Eメールとパスワードを入力してください。");
        setShowAlert(false);
        setTimeout(() => setShowAlert(true), 0);
        return;
      }

      const response = await mailLogin(email, password);
      if (!response || !response.ok) {
        setAlertColor("error");
        setAlertMessage(
          t("cover-sign-in.alert_login_fail_msg") || "ログインに失敗しました。確認メールを確認する。またはメールアドレスとパスワードを確認してください。"
        );
        setShowAlert(false);
        setTimeout(() => setShowAlert(true), 0);
      } else {
        if (rememberMe) {
          setCookie("rememberMe", "true", 30);
          setCookie("email", email, 30);
        } else {
          deleteCookie("rememberMe");
          deleteCookie("email");
        }
        const result = await response.json();
        const { user } = result;
        const { error } = await supabase.auth.setSession({
          access_token: user.access_token,
          refresh_token: user.refresh_token,
        });

        if (error) {
          console.error("セッション設定エラー:", error.message);
          setAlertColor("error");
          setAlertMessage(t("cover-sign-in.alert_session_fail") || "セッションの更新に失敗しました。");
          setShowAlert(true);
          return;
        }

        await refresh();

        setTimeout(() => {
          navigate("/dashboard");
        }, 10);
      }
    } catch (err) {
      console.error(`login err :${JSON.stringify(err)}`);
      setAlertColor("error");
      setAlertMessage(t("cover-sign-in.alert_login_err") || "ログインに失敗しました。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CoverLayout image={bgImage}>
        <Card>
          <MDBox
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={2}
            mt={-3}
            p={3}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              {t("cover-sign-in.Sign_in")}
            </MDTypography>
            <MDTypography display="block" variant="button" color="white" my={1}>
              {t("cover-sign-in.Enter_your")}
            </MDTypography>
          </MDBox>
          <MDBox pt={4} pb={3} px={3}>
            {showAlert && (
              <MDBox mb={2}>
                <MDAlert color={alertColor as any} dismissible onClose={() => setShowAlert(false)}>
                  {alertMessage}
                </MDAlert>
              </MDBox>
            )}

            <MDBox display="flex" justifyContent="flex-end" mb={2} gap={1}>
              <MDButton
                variant={language === "ja" ? "contained" : "outlined"}
                color="info"
                size="small"
                onClick={() => setLanguage?.("ja")}
                sx={{ px: 2, py: 0.5, fontSize: "0.75rem" }}
              >
                日本語
              </MDButton>
              <MDButton
                variant={language === "en" ? "contained" : "outlined"}
                color="info"
                size="small"
                onClick={() => setLanguage?.("en")}
                sx={{ px: 2, py: 0.5, fontSize: "0.75rem" }}
              >
                English
              </MDButton>
            </MDBox>

            <MDBox component="form" role="form" onSubmit={(e: any) => e.preventDefault()}>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label={t("cover-sign-in.Email")}
                  variant="standard"
                  fullWidth
                  placeholder={t("cover-sign-in.email_ph") || "john@example.com"}
                  InputLabelProps={{ shrink: true }}
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  error={emailError}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label={t("cover-sign-in.Password")}
                  variant="standard"
                  fullWidth
                  placeholder={t("cover-sign-in.password_ph") || "************"}
                  InputLabelProps={{ shrink: true }}
                  value={password}
                  onChange={(e: any) => setPassword(e.target.value)}
                  error={passwordError}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>
              
              <MDBox display="flex" alignItems="center" justifyContent="space-between" ml={-1}>
                <MDBox display="flex" alignItems="center">
                  <Switch checked={rememberMe} onChange={handleSetRememberMe} />
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    onClick={handleSetRememberMe}
                    sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                  >
                    &nbsp;&nbsp;{t("cover-sign-in.Remember_me")}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" flexDirection="column" alignItems="flex-end">
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="info"
                    onClick={handleForgotPasswordClick}
                    sx={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {t("cover-sign-in.forgot_password") || "パスワードを忘れた場合"}
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="info"
                    onClick={handleResendEmailClick}
                    sx={{ cursor: "pointer", userSelect: "none", mt: 0.5 }}
                  >
                    {t("cover-sign-in.resend_confirmation") || "確認メールを再送信する"}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  onClick={handleLogin}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("cover-sign-in.Sign_in")
                  )}
                </MDButton>
              </MDBox>
              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color="text">
                  {t("cover-sign-in.Do_Not")}{" "}
                  <MDTypography
                    component={Link}
                    to="/pages/auth/Sign-up"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    {t("cover-sign-in.Sign_up")}
                  </MDTypography>
                </MDTypography>
              </MDBox>

            </MDBox>
          </MDBox>
        </Card>
      </CoverLayout>

      {/* パスワード再設定用ダイアログ */}
      <Dialog
        open={resetDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setResetDialogOpen(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        PaperProps={{
          style: { borderRadius: 12, padding: 8 } 
        }}
      >
        <DialogTitle id="alert-dialog-slide-title">
          <MDTypography component="span" variant="h5" fontWeight="medium" color="dark">
            {t("cover-sign-in.alert_reset_title") || "パスワードの再設定"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" id="alert-dialog-slide-description">
            {t("cover-sign-in.alert_reset_msg") || "入力されたEメールアドレス宛に、パスワード再設定用のメールを送信しますか？"}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton 
            variant="text" 
            color="secondary" 
            onClick={() => setResetDialogOpen(false)}
          >
            {t("cover-sign-in.alert_cancel") || "キャンセル"}
          </MDButton>
          <MDButton 
            variant="gradient" 
            color="info" 
            onClick={handleConfirmResetPassword}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("cover-sign-in.alert_send") || "送信"
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* 確認メール再送信用ダイアログ */}
      <Dialog
        open={resendDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setResendDialogOpen(false)}
        aria-labelledby="resend-dialog-slide-title"
        aria-describedby="resend-dialog-slide-description"
        PaperProps={{
          style: { borderRadius: 12, padding: 8 } 
        }}
      >
        <DialogTitle id="resend-dialog-slide-title">
          <MDTypography component="span" variant="h5" fontWeight="medium" color="dark">
            {t("cover-sign-in.alert_resend_title") || "確認メールの再送信"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" id="resend-dialog-slide-description">
            {t("cover-sign-in.alert_resend_msg") || "入力されたEメールアドレス宛に、アカウント確認メールを再送信しますか？"}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton 
            variant="text" 
            color="secondary" 
            onClick={() => setResendDialogOpen(false)}
          >
            {t("cover-sign-in.alert_cancel") || "キャンセル"}
          </MDButton>
          <MDButton 
            variant="gradient" 
            color="info" 
            onClick={handleConfirmResendEmail}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("cover-sign-in.alert_send") || "送信"
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SignIn;
